import { CalendarOff, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { SetAvailabilityInput } from '@nest-recruit/shared';
import { Button } from './Button';
import { Card } from './Card';
import { DatePicker } from './DatePicker';
import { Field } from './Field';
import { Input } from './Input';
import { Select } from './Select';
import { TimePicker } from './TimePicker';
import { DEFAULT_TIME_ZONE, formatDate } from '../lib/format';
import { useNow } from '../lib/use-now';
import styles from './AvailabilityEditor.module.css';

/**
 * F2.12 — declared availability: usual weekly hours, and dated time away. The only
 * source of availability in the system (D5, §L5). Used on USER-02 by the HR Lead and on
 * "My availability" by everyone (S19) — moved here from the admin feature for that.
 *
 * S19 Q1: nothing declared means the person's working calendar; declaring narrows it.
 * Hours are wall-clock times in the person's own timezone; days away are whole days,
 * first to last.
 */

/** 0 = Sunday, matching the D14 Sunday–Thursday working week. */
const DAYS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
] as const;

/** One declared row, as the users API returns it. */
export interface DeclaredAvailability {
  readonly id?: string;
  readonly dayOfWeek: number | null;
  readonly startTime: string | null;
  readonly endTime: string | null;
  readonly blockedFrom: string | null;
  readonly blockedTo: string | null;
  readonly reason: string | null;
}

interface WeeklyDraft {
  readonly key: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

interface BlockDraft {
  readonly key: string;
  blockedFrom: string;
  blockedTo: string;
  reason: string;
}

let nextKey = 0;
const newKey = (): string => `slot-${(nextKey += 1)}`;

interface AvailabilityEditorProps {
  readonly rows: readonly DeclaredAvailability[];
  readonly canEdit: boolean;
  readonly isSaving: boolean;
  readonly onSave: (input: SetAvailabilityInput) => void;
  readonly title?: string;
  /** Whose calendar day "Add time off" starts on: the person's own zone, not UTC's date. */
  readonly timeZone?: string;
}

/** The calendar day an instant falls on in a zone, as `YYYY-MM-DD`. */
export function calendarDayIn(now: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(now));
}

export function AvailabilityEditor({ rows, canEdit, isSaving, onSave, title = 'When they are available', timeZone = DEFAULT_TIME_ZONE }: AvailabilityEditorProps): ReactNode {
  const now = useNow();
  const [weekly, setWeekly] = useState<readonly WeeklyDraft[]>(() =>
    rows
      .filter((row) => row.dayOfWeek !== null)
      .map((row) => ({
        key: newKey(),
        dayOfWeek: String(row.dayOfWeek),
        startTime: (row.startTime ?? '09:45').slice(0, 5),
        endTime: (row.endTime ?? '18:45').slice(0, 5),
      })),
  );
  const [blocks, setBlocks] = useState<readonly BlockDraft[]>(() =>
    rows
      .filter((row) => row.blockedFrom !== null)
      .map((row) => ({
        key: newKey(),
        blockedFrom: (row.blockedFrom ?? '').slice(0, 10),
        blockedTo: (row.blockedTo ?? '').slice(0, 10),
        reason: row.reason ?? '',
      })),
  );

  const badHours = weekly.some((slot) => slot.endTime <= slot.startTime);
  const badBlocks = blocks.some((block) => block.blockedFrom === '' || block.blockedTo === '' || block.blockedTo < block.blockedFrom);

  const updateWeekly = (key: string, patch: Partial<WeeklyDraft>): void =>
    setWeekly((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  const updateBlock = (key: string, patch: Partial<BlockDraft>): void =>
    setBlocks((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)));

  const submit = (): void =>
    onSave({
      weekly: weekly.map((slot) => ({ dayOfWeek: Number(slot.dayOfWeek), startTime: slot.startTime, endTime: slot.endTime })),
      blockOuts: blocks.map((block) => ({
        blockedFrom: `${block.blockedFrom}T00:00:00.000Z`,
        blockedTo: `${block.blockedTo}T00:00:00.000Z`,
        reason: block.reason.trim() === '' ? null : block.reason.trim(),
      })),
    });

  return (
    <Card
      title={title}
      meta="Interview scheduling reads this — there is no calendar to fall back on"
      action={
        canEdit ? (
          <Button variant="solid" size="sm" isPending={isSaving} disabled={badHours || badBlocks} onClick={submit}>
            Save availability
          </Button>
        ) : undefined
      }
    >
      <section className={styles.section}>
        <h3 className={styles.heading}>Usual working hours</h3>
        {weekly.length === 0 && <p className={styles.quiet}>Nothing set, so interviews can be booked any time in the working calendar. Add hours to narrow it.</p>}

        <ul className={styles.rows}>
          {weekly.map((slot) => (
            <li key={slot.key} className={styles.weeklyRow}>
              <Field label="Day">
                {({ inputId }) => (
                  <Select id={inputId} label="Day" value={slot.dayOfWeek} disabled={!canEdit} options={[...DAYS]} onChange={(value) => updateWeekly(slot.key, { dayOfWeek: value })} />
                )}
              </Field>
              <Field label="From">
                {({ inputId }) => <TimePicker id={inputId} label="From" value={slot.startTime} disabled={!canEdit} onChange={(value) => updateWeekly(slot.key, { startTime: value })} />}
              </Field>
              <Field label="Until" {...(slot.endTime <= slot.startTime ? { error: 'The end must be after the start.' } : {})}>
                {({ inputId, isInvalid }) => <TimePicker id={inputId} label="Until" value={slot.endTime} isInvalid={isInvalid} disabled={!canEdit} onChange={(value) => updateWeekly(slot.key, { endTime: value })} />}
              </Field>
              {canEdit && (
                <Button variant="ghost" size="sm" icon={Trash2} aria-label="Remove these hours" onClick={() => setWeekly((current) => current.filter((item) => item.key !== slot.key))} />
              )}
            </li>
          ))}
        </ul>

        {canEdit && (
          <Button
            variant="quiet"
            size="sm"
            icon={Plus}
            onClick={() => setWeekly((current) => [...current, { key: newKey(), dayOfWeek: '0', startTime: '09:45', endTime: '18:45' }])}
          >
            Add working hours
          </Button>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.heading}>Away from work</h3>
        {blocks.length === 0 && <p className={styles.quiet}>No time off recorded.</p>}

        <ul className={styles.rows}>
          {blocks.map((block) => (
            <li key={block.key} className={styles.blockRow}>
              <Field label="First day">
                {({ inputId }) => <DatePicker id={inputId} label="First day away" value={block.blockedFrom} disabled={!canEdit} onChange={(value) => updateBlock(block.key, { blockedFrom: value, blockedTo: block.blockedTo < value ? value : block.blockedTo })} />}
              </Field>
              <Field label="Last day" {...(block.blockedTo < block.blockedFrom ? { error: "The last day can't be before the first." } : {})}>
                {({ inputId, isInvalid }) => <DatePicker id={inputId} label="Last day away" value={block.blockedTo} min={block.blockedFrom} isInvalid={isInvalid} disabled={!canEdit} onChange={(value) => updateBlock(block.key, { blockedTo: value })} />}
              </Field>
              <Field label="Why" isOptional>
                {({ inputId }) => <Input id={inputId} value={block.reason} disabled={!canEdit} placeholder="Annual leave" onChange={(event) => updateBlock(block.key, { reason: event.target.value })} />}
              </Field>
              {canEdit && (
                <Button variant="ghost" size="sm" icon={Trash2} aria-label="Remove this time off" onClick={() => setBlocks((current) => current.filter((item) => item.key !== block.key))} />
              )}
            </li>
          ))}
        </ul>

        {canEdit && (
          <Button
            variant="quiet"
            size="sm"
            icon={CalendarOff}
            onClick={() => {
              const today = calendarDayIn(now, timeZone);
              setBlocks((current) => [...current, { key: newKey(), blockedFrom: today, blockedTo: today, reason: '' }]);
            }}
          >
            Add time off
          </Button>
        )}
      </section>
    </Card>
  );
}

/** The one-line summary on USER-02. */
export function describeAvailability(rows: readonly DeclaredAvailability[]): string {
  const weekly = rows.filter((row) => row.dayOfWeek !== null).length;
  const blocks = rows.filter((row) => row.blockedFrom !== null);
  const parts: string[] = [];
  if (weekly > 0) parts.push(`${weekly} working day${weekly === 1 ? '' : 's'}`);
  if (blocks.length > 0) {
    const next = blocks[0];
    parts.push(`away from ${next?.blockedFrom ? formatDate(next.blockedFrom) : ''}`);
  }
  return parts.length === 0 ? 'Working calendar hours' : parts.join(' · ');
}
